/*
 * Team Setup — manual team assignment and captain picker.
 *
 * Layout:
 *   Top strip: one compact card per team (color dot · name · captain · count
 *              · Edit branding). Always two-up at desktop width.
 *   Roster:    auto-fit grid of team columns. Each cell lists that team's
 *              players. The captain has a filled gold star; tap it to
 *              remove. Tap an empty star to promote. A small "→ Other"
 *              move button on the right swaps the player to the other
 *              team (or, with 3+ teams, opens a tiny chip picker).
 *   Unassigned players (rare with the seed script, but possible after
 *   adding mid-event) get their own column so they don't get lost in a
 *   wall of team rows.
 *
 * Why this shape: with 24 players the old "all players in one long list +
 * three team chips each" took half a mile of vertical scroll. Grouping by
 * team and putting each roster in its own column halves the scroll, keeps
 * the captain prominent, and reads at a glance.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Star, GripVertical, Pencil, Plus, X } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { TeamAvatar } from '@/components/TeamAvatar';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { getInviteStatus, inviteStatusLabel, inviteStatusColor } from '@/lib/inviteStatus';
import type { EventPlayer, TournamentTeam } from '@golf-app/shared';

/** Plus-handicap notation with one-decimal precision:
 *  -2 → "+2", -2.5 → "+2.5", 0 → "0", 5 → "5", 12.5 → "12.5". */
function fmtHcp(h: number | null | undefined): string | null {
  if (h == null) return null;
  const abs = Math.abs(h);
  const rounded = Math.round(abs * 10) / 10;
  const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return h < 0 ? `+${str}` : str;
}

/** Sort key for a player's handicap. Lower is better in golf (plus
 *  handicaps are negative numbers). Players with no handicap sort last
 *  so the visible roster stays interpretable. */
function hcpSortKey(p: EventPlayer): number {
  return p.eventHandicapIndex ?? Number.POSITIVE_INFINITY;
}
function byHandicap(a: EventPlayer, b: EventPlayer): number {
  const ha = hcpSortKey(a);
  const hb = hcpSortKey(b);
  if (ha !== hb) return ha - hb;
  // Tiebreaker: alphabetical by display name (case-insensitive). Important
  // when several players are missing a handicap — without this they'd
  // appear in arbitrary creation order.
  return (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' });
}

/** Sort that pins the captain to the top of the team column, then falls
 *  back to handicap order for everyone else. Captains read first because
 *  they're the team identity — a roster always opens with "led by X." */
function captainFirstThenHandicap(captainId: string | null) {
  return (a: EventPlayer, b: EventPlayer): number => {
    if (captainId) {
      if (a.id === captainId) return -1;
      if (b.id === captainId) return 1;
    }
    return byHandicap(a, b);
  };
}

export default function TeamSetup() {
  const { eventId } = useParams<{ eventId: string }>();
  const { activeEvent, setActiveEvent } = useTournamentStore();
  const [event, setEvent] = useState(activeEvent?.id === eventId ? activeEvent : null);
  const [loading, setLoading] = useState(!event);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  // Mobile-first fallback for drag-and-drop. iOS Safari/Chrome don't
  // fire native HTML5 drag events, so tapping a player opens an action
  // sheet with the same options the drag flow would offer. Desktop
  // users can still drag.
  const [movingPlayer, setMovingPlayer] = useState<EventPlayer | null>(null);

  const refetch = useCallback(async () => {
    if (!eventId) return;
    try {
      const ev = await api.getTournament(eventId);
      setEvent(ev);
      setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load");
    } finally {
      setLoading(false);
    }
  }, [eventId, setActiveEvent]);

  useEffect(() => { refetch(); }, [refetch]);

  // Permission gate. Backend rejects writes from anyone other than the
  // event owner or a captain. Without this guard the optimistic UI would
  // flicker the change in and snap back when the API call returns 403.
  // Cheap defensive check that mirrors the backend rule.
  const canEdit = event?.myRole === 'owner' || event?.myRole === 'captain';

  async function assignTeam(player: EventPlayer, teamId: string | null) {
    if (!canEdit) { setError('Only the event owner or a captain can change team assignments.'); return; }
    setError(null);

    // OPTIMISTIC: update local state instantly so the drop feels seamless.
    // The previous teamId is captured for rollback on API failure. We do
    // NOT setBusy here — the UI is already correct, no need to grey out
    // the rest of the screen for a network round-trip.
    const prevTeamId = player.teamId;
    setEvent((curr) => {
      if (!curr) return curr;
      return {
        ...curr,
        players: curr.players.map((p) =>
          p.id === player.id ? { ...p, teamId } : p,
        ),
      };
    });

    try {
      await api.updateTournamentPlayer(player.id, {
        teamId,
        ...(player.roleType === 'captain' ? { roleType: 'player' } : {}),
      });
      // Quiet refetch so the local view stays in sync with any server-side
      // side effects (e.g. the API auto-clears captain status when a
      // captain leaves their team). Failures here are ignorable — local
      // state is already what the user expects.
      refetch().catch(() => { /* swallow — optimistic state holds */ });
    } catch (e: any) {
      // Roll back the optimistic move and surface the error.
      setEvent((curr) => {
        if (!curr) return curr;
        return {
          ...curr,
          players: curr.players.map((p) =>
            p.id === player.id ? { ...p, teamId: prevTeamId } : p,
          ),
        };
      });
      setError(e?.message ?? 'Failed to move player');
    }
  }

  async function makeCaptain(player: EventPlayer) {
    if (!canEdit) { setError('Only the event owner or a captain can set captains.'); return; }
    if (!player.teamId) {
      setError('Assign the player to a team first.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.assignTournamentCaptain(player.teamId, player.id);
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to set captain');
    } finally {
      setBusy(false);
    }
  }

  async function removeCaptain(player: EventPlayer) {
    if (!canEdit) { setError('Only the event owner or a captain can change captains.'); return; }
    if (!player.teamId) return;
    setError(null);
    setBusy(true);
    try {
      await api.removeTournamentCaptain(player.teamId);
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to remove captain');
    } finally {
      setBusy(false);
    }
  }

  async function addTeam() {
    if (!eventId || !canEdit) return;
    const name = newTeamName.trim();
    if (!name) { setError('Give the new team a name.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.createTournamentTeam(eventId, { name });
      setNewTeamName('');
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create team.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (!event) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
        <div className="t-empty"><div className="t-empty-title">Event not found</div></div>
      </AppShell>
    );
  }

  const teams = event.teams;
  const activePlayers = event.players.filter((p) => p.status !== 'removed' && p.roleType !== 'owner');
  // Sort every roster lowest-to-highest handicap. Plus handicaps (negative
  // numbers) end up at the top; no-handicap players sort to the bottom.
  const unassigned = activePlayers.filter((p) => !p.teamId).slice().sort(byHandicap);

  // Per-team aggregates: sum of handicaps (plus handicaps count as
  // negative — they reduce the team total) and member list. Drives the
  // big TEAM HCP card and the balance bar.
  const teamAgg = teams.map((t) => {
    const members = activePlayers.filter((p) => p.teamId === t.id);
    const totalHcp = members.reduce((sum, p) => sum + (p.eventHandicapIndex ?? 0), 0);
    const captain = activePlayers.find((p) => p.id === t.captainEventPlayerId) ?? null;
    return { team: t, members, totalHcp, captain };
  });

  // Balance bar = each team's share of the combined handicap. When both
  // are 0 we just split 50/50 so the bar still renders.
  const totalAllHcp = teamAgg.reduce((s, a) => s + a.totalHcp, 0);
  const balanceFractions = totalAllHcp <= 0
    ? teamAgg.map(() => 1 / Math.max(1, teamAgg.length))
    : teamAgg.map((a) => a.totalHcp / totalAllHcp);

  // Stroke gap = absolute difference between the two highest team totals.
  // For 2 teams that's |Red - Blue|, which is the number we surface.
  // Rounded to a whole number so the label reads "4 stroke gap" instead of
  // "4.199999999999989 stroke gap" — the underlying handicaps are one-
  // decimal values, so summing 12 of them produces float noise we don't
  // want in the UI.
  const gap = teamAgg.length >= 2
    ? Math.round(Math.abs(teamAgg[0].totalHcp - teamAgg[1].totalHcp))
    : 0;
  // Color the gap label by severity. ≤ 5 is balanced, ≤ 15 is acceptable,
  // anything more is loud — captains should rebalance.
  const gapColor = gap <= 5 ? 'var(--green-bright)'
                  : gap <= 15 ? 'var(--gold)'
                  : '#f97316'; // orange-500

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero">
        <h1>Teams & Captains</h1>
        <p>Build as many teams as the event needs. Tap a player to move them between teams or set a captain. On desktop you can also drag rows between columns.</p>
      </section>

      {canEdit ? <div className="t-field" style={{ maxWidth: 420 }}>
        <label className="t-label" htmlFor="new-team-name">Add a team</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input id="new-team-name" className="t-input" value={newTeamName} onChange={(event) => setNewTeamName(event.target.value)} maxLength={50} placeholder="e.g. Team Green" />
          <button type="button" className="t-btn t-btn-primary" disabled={busy || !newTeamName.trim()} onClick={addTeam}><Plus /> Add</button>
        </div>
      </div> : null}


      {error && <div className="t-error">{error}</div>}

      {/* SCOREBOARD — one combined card spanning the page. Left side is
          Team A (dot · name · count · big HCP). Center is the balance
          label and proportional bar. Right side mirrors with Team B.
          On mobile (<= 540px) the three sections stack vertically:
          Team A row / balance bar / Team B row. */}
      {teamAgg.length === 2 && (
        <div
          className="ts-scoreboard"
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            padding: '16px 20px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            minWidth: 0,
          }}
        >
          <ScoreboardSide
            team={teamAgg[0].team}
            count={teamAgg[0].members.length}
            captain={teamAgg[0].captain}
            totalHcp={teamAgg[0].totalHcp}
            align="left"
            eventId={eventId!}
          />

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: gapColor, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
              {gap === 0 ? 'Perfectly even' : `${gap} stroke gap`}
            </span>
            <div
              style={{
                display: 'flex',
                width: '100%',
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
                background: 'var(--border)',
              }}
            >
              {teamAgg.map((a, i) => (
                <div
                  key={a.team.id}
                  style={{
                    width: `${balanceFractions[i] * 100}%`,
                    background: a.team.primaryColor ?? 'var(--text-mut)',
                    transition: 'width 200ms ease',
                  }}
                  title={`${a.team.name}: ${fmtHcp(a.totalHcp) ?? '0'}`}
                />
              ))}
            </div>
          </div>

          <ScoreboardSide
            team={teamAgg[1].team}
            count={teamAgg[1].members.length}
            captain={teamAgg[1].captain}
            totalHcp={teamAgg[1].totalHcp}
            align="right"
            eventId={eventId!}
          />
        </div>
      )}

      {teamAgg.length > 2 && (
        <div
          className="ts-scoreboard"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {teamAgg.map((aggregate) => (
            <div
              key={aggregate.team.id}
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: '16px 20px',
                minWidth: 0,
              }}
            >
              <ScoreboardSide
                team={aggregate.team}
                count={aggregate.members.length}
                captain={aggregate.captain}
                totalHcp={aggregate.totalHcp}
                align="left"
                eventId={eventId!}
              />
            </div>
          ))}
        </div>
      )}

      {/* ROSTER GRID — three columns side by side, matching the scoreboard
          on top: Team A (left) · Unassigned (middle) · Team B (right).
          The team columns sit next to their scoreboard side; Unassigned is
          the neutral pool in the center so you can drag players to either
          team without crossing through the other team's column.
          On narrow screens the auto-fit collapses to whatever fits.
          Default `align-items: stretch` here is intentional: every grid
          cell grows to match the tallest column (usually Unassigned),
          which lets each team's drop zone extend through the empty
          space below the card so the captain can drop anywhere in the
          column, not just on the small visible card. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(280px, 1fr))`,
          gap: 10,
        }}
      >
        {/* LEFT — Team A. Rendered before Unassigned so it lines up with
            the left scoreboard side. */}
        {teamAgg[0] && (
          <RosterColumn
            key={teamAgg[0].team.id}
            title={teamAgg[0].team.name}
            accent={teamAgg[0].team.primaryColor ?? 'var(--text-mut)'}
            team={teamAgg[0].team}
            players={teamAgg[0].members.slice().sort(captainFirstThenHandicap(teamAgg[0].team.captainEventPlayerId ?? null))}
            teams={teams}
            busy={busy}
            showRemove={true}
            onToggleCaptain={(p, isCap) => isCap ? removeCaptain(p) : makeCaptain(p)}
            onRemoveFromTeam={(p) => assignTeam(p, null)}
            onTapPlayer={(p) => canEdit && setMovingPlayer(p)}
            onDropPlayerId={(playerId) => {
              const p = activePlayers.find((x) => x.id === playerId);
              if (p && p.teamId !== teamAgg[0].team.id) assignTeam(p, teamAgg[0].team.id);
            }}
          />
        )}

        {/* MIDDLE — Unassigned. Lives between the two team columns so a
            player can drop into either team without crossing. */}
        <RosterColumn
          title="Unassigned"
          accent="var(--text-mut)"
          players={unassigned}
          teams={teams}
          busy={busy}
          showRemove={false}
          onToggleCaptain={(p, isCap) => isCap ? removeCaptain(p) : makeCaptain(p)}
          onRemoveFromTeam={() => { /* never called when showRemove=false */ }}
          onTapPlayer={(p) => canEdit && setMovingPlayer(p)}
          onDropPlayerId={(playerId) => {
            const p = activePlayers.find((x) => x.id === playerId);
            if (p && p.teamId !== null) assignTeam(p, null);
          }}
        />

        {/* RIGHT — Team B. Mirrors the right scoreboard side. */}
        {teamAgg.slice(1).map(({ team: t, members }) => (
          <RosterColumn
            key={t.id}
            title={t.name}
            accent={t.primaryColor ?? 'var(--text-mut)'}
            team={t}
            players={members.slice().sort(captainFirstThenHandicap(t.captainEventPlayerId ?? null))}
            teams={teams}
            busy={busy}
            showRemove={true}
            onToggleCaptain={(p, isCap) => isCap ? removeCaptain(p) : makeCaptain(p)}
            onRemoveFromTeam={(p) => assignTeam(p, null)}
            onTapPlayer={(p) => canEdit && setMovingPlayer(p)}
            onDropPlayerId={(playerId) => {
              const p = activePlayers.find((x) => x.id === playerId);
              if (p && p.teamId !== t.id) assignTeam(p, t.id);
            }}
          />
        ))}
      </div>

      {/* Mobile-first move sheet. Opens on player tap because native
          HTML5 drag-and-drop doesn't fire on iOS. Renders the same
          actions a drag would have offered: move to any team /
          unassigned, toggle captain. */}
      {movingPlayer && (
        <PlayerMoveSheet
          player={movingPlayer}
          teams={teams}
          busy={busy}
          onClose={() => setMovingPlayer(null)}
          onMove={(teamId) => {
            assignTeam(movingPlayer, teamId);
            setMovingPlayer(null);
          }}
          onToggleCaptain={() => {
            const isCap = movingPlayer.id === teams.find((t) => t.id === movingPlayer.teamId)?.captainEventPlayerId;
            if (isCap) removeCaptain(movingPlayer);
            else makeCaptain(movingPlayer);
            setMovingPlayer(null);
          }}
        />
      )}
    </AppShell>
  );
}

// ── PlayerMoveSheet ───────────────────────────────────────────────────────
//
// Modal that opens on player tap as the touch-friendly alternative to
// drag-and-drop. Renders the same set of actions the drag flow would
// have offered:
//   - Move to each team (skipping the player's current team)
//   - Move to Unassigned (skipped when already unassigned)
//   - Make / remove captain (only if on a team)
//   - Cancel
// Each action button lands at ≥48px tall so phone users can hit them
// without precision.

function PlayerMoveSheet({
  player, teams, busy, onClose, onMove, onToggleCaptain,
}: {
  player: EventPlayer;
  teams: TournamentTeam[];
  busy: boolean;
  onClose: () => void;
  onMove: (teamId: string | null) => void;
  onToggleCaptain: () => void;
}) {
  const currentTeam = teams.find((t) => t.id === player.teamId) ?? null;
  const isCaptain = currentTeam ? currentTeam.captainEventPlayerId === player.id : false;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.66)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: 0,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Move ${player.displayName ?? 'player'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          width: '100%',
          maxWidth: 520,
          padding: 16,
          display: 'flex', flexDirection: 'column', gap: 10,
          boxShadow: '0 -16px 40px rgba(0,0,0,0.45)',
          // Cap + scroll: in landscape or with 3+ teams the action list
          // outgrows the viewport and the header was clipped off-screen
          // with no way to reach it.
          maxHeight: '85dvh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
          <PlayerAvatar avatarUrl={player.avatarUrl} displayName={player.displayName} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {player.displayName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-mut)', marginTop: 2 }}>
              {currentTeam ? `On ${currentTeam.name}${isCaptain ? ' · Captain' : ''}` : 'Unassigned'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-mut)', cursor: 'pointer', padding: 8 }}
          >
            <X width={18} height={18} />
          </button>
        </div>

        {teams.map((t) => {
          const here = t.id === player.teamId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onMove(t.id)}
              disabled={busy || here}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: here ? 'var(--card-head)' : 'var(--bg)',
                border: `1px solid ${here ? (t.primaryColor ?? 'var(--border)') : 'var(--border)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                minHeight: 48,
                color: 'var(--text)',
                fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
                textAlign: 'left',
                cursor: (busy || here) ? 'not-allowed' : 'pointer',
                opacity: here ? 0.6 : 1,
              }}
            >
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: t.primaryColor ?? 'var(--text-mut)' }} />
              <span style={{ flex: 1 }}>Move to {t.name}</span>
              {here && <span style={{ fontSize: 11, color: 'var(--text-mut)', fontWeight: 700 }}>Already here</span>}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => onMove(null)}
          disabled={busy || player.teamId === null}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: player.teamId === null ? 'var(--card-head)' : 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '12px 14px',
            minHeight: 48,
            color: 'var(--text)',
            fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
            textAlign: 'left',
            cursor: (busy || player.teamId === null) ? 'not-allowed' : 'pointer',
            opacity: player.teamId === null ? 0.6 : 1,
          }}
        >
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'var(--text-mut)' }} />
          <span style={{ flex: 1 }}>Move to Unassigned</span>
          {player.teamId === null && <span style={{ fontSize: 11, color: 'var(--text-mut)', fontWeight: 700 }}>Already here</span>}
        </button>

        {/* Captain toggle. Only meaningful when the player is on a team. */}
        {currentTeam && (
          <button
            type="button"
            onClick={onToggleCaptain}
            disabled={busy}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: isCaptain ? 'rgba(212,175,55,0.10)' : 'var(--bg)',
              border: `1px solid ${isCaptain ? 'var(--gold)' : 'var(--border)'}`,
              borderRadius: 10,
              padding: '12px 14px',
              minHeight: 48,
              color: isCaptain ? 'var(--gold)' : 'var(--text)',
              fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
              textAlign: 'left',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            <Star
              width={16} height={16}
              fill={isCaptain ? 'var(--gold)' : 'none'}
              stroke="currentColor"
              strokeWidth={2.5}
            />
            <span style={{ flex: 1 }}>
              {isCaptain ? `Remove as captain of ${currentTeam.name}` : `Make captain of ${currentTeam.name}`}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '12px 14px',
            minHeight: 48,
            color: 'var(--text-sec)',
            fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
            marginTop: 4,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── ScoreboardSide ─────────────────────────────────────────────────────────
// One side of the combined Teams scoreboard. Left side renders dot · meta
// · big HCP. Right side mirrors with HCP · meta · dot. The team name acts
// as the entry point to Team Branding (subtle underline on hover).
function ScoreboardSide({
  team, count, captain, totalHcp, align, eventId,
}: {
  team: TournamentTeam;
  count: number;
  captain: EventPlayer | null;
  totalHcp: number;
  align: 'left' | 'right';
  eventId: string;
}) {
  const color = team.primaryColor ?? 'var(--text-mut)';
  // Avatar = team logo if uploaded, otherwise a colored dot in the team
  // color. Sized larger here than in row chrome so the scoreboard reads
  // as a banner element.
  const dot = <TeamAvatar team={team} size={28} />;
  // Three-line hierarchy per Travis's spec:
  //   1) Team name  — largest, the hero
  //   2) Captain: NAME — medium, second priority
  //   3) N players — smallest, third
  const meta = (
    <div style={{ minWidth: 0, textAlign: align, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Link
        to={`/app/tournaments/${eventId}/teams/${team.id}/branding`}
        title="Edit team branding"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          // Pencil sits opposite the team avatar so it never crowds the
          // logo: right of the name on Team A (left side), left of the
          // name on Team B (right side).
          flexDirection: align === 'right' ? 'row-reverse' : 'row',
          textDecoration: 'none', color: 'var(--text)',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 22, fontWeight: 900, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            minWidth: 0,
          }}
        >
          {team.name}
        </span>
        <span
          aria-hidden
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, borderRadius: 11,
            border: '1px solid var(--border)',
            background: 'var(--card-head, transparent)',
            color: 'var(--text-mut)',
            flexShrink: 0,
          }}
        >
          <Pencil width={11} height={11} strokeWidth={2.5} />
        </span>
      </Link>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: align === 'right' ? 'flex-end' : 'flex-start', minWidth: 0 }}>
        <Star
          width={12} height={12}
          fill={captain ? 'var(--gold)' : 'none'}
          stroke={captain ? 'var(--gold)' : 'var(--text-mut)'}
          strokeWidth={2.5}
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {captain ? <>Captain: <span style={{ fontWeight: 800 }}>{captain.displayName}</span></> : <span style={{ color: 'var(--text-mut)', fontWeight: 600 }}>No captain yet</span>}
        </span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-mut)' }}>
        {count} player{count !== 1 ? 's' : ''}
      </div>
    </div>
  );
  // Stacked label + number so it's obvious what the big figure
  // represents. The "TEAM HCP" caption sits above the value at a small
  // muted size; the value itself is the visual headline.
  const hcp = (
    <div
      style={{
        display: 'flex', flexDirection: 'column',
        alignItems: align === 'left' ? 'flex-start' : 'flex-end',
        flexShrink: 0, lineHeight: 1,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-mut)', marginBottom: 2 }}>
        Team HCP
      </span>
      <span style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {fmtHcp(totalHcp) ?? '0'}
      </span>
    </div>
  );

  return (
    <div
      className="ts-scoreboard-side"
      style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexShrink: 0 }}
    >
      {align === 'left' ? (
        <>
          {dot}
          {meta}
          {hcp}
        </>
      ) : (
        <>
          {hcp}
          {meta}
          {dot}
        </>
      )}
    </div>
  );
}

// ── RosterColumn ───────────────────────────────────────────────────────────
// Both a list view and a drop target. Each player row is draggable; on
// drop, onDropPlayerId fires with the dragged player's event_player id and
// the parent decides what to do (move to this team, or to Unassigned).
//
// showRemove turns on the small "Remove" button at the right of each row
// — used in team columns so the captain has a one-click "take this person
// off my team" path. Hidden in the Unassigned column where it would be a
// no-op.
function RosterColumn({
  title, accent, team, players, teams, busy, showRemove, onToggleCaptain, onRemoveFromTeam, onDropPlayerId, onTapPlayer,
}: {
  title: string;
  accent: string;
  /** The team this column represents. Omitted for the Unassigned column —
   *  there's no logo or color for "no team", so it falls back to the
   *  generic accent dot. */
  team?: TournamentTeam;
  players: EventPlayer[];
  /** Used only to look up captain status per player. */
  teams: TournamentTeam[];
  busy: boolean;
  showRemove: boolean;
  onToggleCaptain: (player: EventPlayer, isCaptain: boolean) => void;
  onRemoveFromTeam: (player: EventPlayer) => void;
  onDropPlayerId: (playerId: string) => void;
  /** Fires when the user TAPS a player row (vs drags). Drives the
   *  mobile fallback action sheet so phone users can build teams
   *  without HTML5 drag-and-drop (which iOS doesn't support). */
  onTapPlayer?: (player: EventPlayer) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      // Outer wrapper. This is the actual drop target. It fills the
      // entire grid cell (height: 100% via grid stretch), so dropping
      // anywhere below the visible card still lands in this column.
      onDragOver={(e) => {
        // preventDefault marks this element as a valid drop target.
        // Without it the browser refuses the drop.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // dragleave fires when crossing into a child element too, so we
        // only flip off when the pointer truly leaves the wrapper.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setDragOver(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const playerId = e.dataTransfer.getData('text/plain');
        if (playerId) onDropPlayerId(playerId);
      }}
      style={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // When the user is dragging anywhere inside this column (including
        // the empty tail), tint the whole wrapper so they can see the
        // entire area is a valid drop zone.
        background: dragOver ? `${accent}10` : 'transparent',
        borderRadius: 12,
        outline: dragOver ? `1px dashed ${accent}` : 'none',
        outlineOffset: 0,
        transition: 'background 80ms ease, outline-color 80ms ease',
        // A bit of bottom padding so the dashed outline doesn't hug
        // the very last hint when the cell stretches well below the card.
        paddingBottom: 8,
      }}
    >
    <div
      style={{
        background: dragOver ? `${accent}1a` : 'var(--card)',
        border: `1px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? accent : 'var(--border)'}`,
        borderRadius: 10,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minWidth: 0,
        transition: 'background 80ms ease, border-color 80ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        {team ? (
          <TeamAvatar team={team} size={14} />
        ) : (
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: accent, flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 15, fontWeight: 900, color: accent, letterSpacing: 0.6, textTransform: 'uppercase', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-mut)' }}>
          {players.length}
        </span>
      </div>

      {players.length === 0 ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 56,
            border: `1.5px dashed ${accent}`,
            opacity: 0.55,
            borderRadius: 6,
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
            color: accent,
            padding: 6,
          }}
        >
          Drop players here
        </div>
      ) : (
        players.map((p) => {
          const isCaptain = teams.some((t) => t.captainEventPlayerId === p.id);
          const hcp = fmtHcp(p.eventHandicapIndex);
          const captainable = Boolean(p.teamId);
          // Team-tinted pill: faint team-color background with a 1px
          // team-color border. Captains get a slightly stronger fill so
          // they read first when you scan the column.
          const rowBg = showRemove
            ? (isCaptain ? `${accent}26` : `${accent}14`)
            : (isCaptain ? 'rgba(212, 175, 55, 0.08)' : 'transparent');
          const rowBorder = showRemove
            ? accent
            : (isCaptain ? 'var(--gold)' : 'transparent');

          return (
            <div
              key={p.id}
              draggable={!busy}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', p.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onClick={() => {
                if (busy || !onTapPlayer) return;
                onTapPlayer(p);
              }}
              role="button"
              tabIndex={0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                minHeight: 44,
                borderRadius: 8,
                background: rowBg,
                border: `1px solid ${rowBorder}`,
                minWidth: 0,
                // 'grab' on desktop hints at drag-to-move. The tap
                // fallback is the primary on mobile, but a hover
                // pointer wouldn't hurt either.
                cursor: busy ? 'wait' : (onTapPlayer ? 'pointer' : 'grab'),
                userSelect: 'none',
              }}
            >
              {/* Drag handle — purely visual cue that this row is draggable.
                  The whole row is the actual drag source, so this icon is
                  pointer-events:none to avoid stealing the drag event. */}
              <GripVertical
                width={13} height={13}
                style={{ color: 'var(--text-mut)', opacity: 0.6, flexShrink: 0, pointerEvents: 'none' }}
              />

              {/* Player avatar — photo if uploaded, monogram fallback.
                  Sits immediately left of the name so the row reads as
                  "person · star · name" at a glance. */}
              <PlayerAvatar avatarUrl={p.avatarUrl} displayName={p.displayName} size={22} />

              <button
                type="button"
                onClick={(e) => {
                  // Prevent the parent row tap from also firing.
                  e.stopPropagation();
                  onToggleCaptain(p, isCaptain);
                }}
                disabled={busy || !captainable}
                title={
                  !captainable ? 'Assign to a team first'
                  : isCaptain  ? 'Remove as captain'
                                : 'Make captain'
                }
                style={{
                  // 32x32 tap target so phone users can reliably set
                  // a captain without zooming. Was a 15×15 icon with
                  // zero padding, which made it almost impossible to
                  // hit on a phone without also tapping the player row.
                  background: 'none', border: 'none', margin: 0,
                  width: 32, height: 32,
                  borderRadius: 8,
                  cursor: captainable ? 'pointer' : 'not-allowed',
                  color: isCaptain ? 'var(--gold)' : 'var(--text)',
                  opacity: captainable ? 1 : 0.4,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Star
                  width={18} height={18}
                  fill={isCaptain ? 'var(--gold)' : 'none'}
                  stroke="currentColor"
                  strokeWidth={2.5}
                />
              </button>

              <span
                style={{
                  fontSize: 15, fontWeight: 800, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  // 60px floor — desktop Safari's nested-flex shrink could
                  // collapse the name to a sliver next to the pills.
                  minWidth: 60, flex: '0 1 auto', lineHeight: 1.2,
                }}
              >
                {p.displayName}
              </span>

              {/* Invite-status dot. A tiny colored circle right after the
                  name so the captain can scan and see who's joined
                  (green), who's pending (gold), and who hasn't been
                  invited yet (grey). Tooltip carries the label so we
                  don't widen the already-packed row. */}
              {(() => {
                const s = getInviteStatus(p);
                const c = inviteStatusColor(s);
                return (
                  <span
                    title={inviteStatusLabel(s)}
                    aria-label={inviteStatusLabel(s)}
                    style={{
                      display: 'inline-block',
                      width: 8, height: 8,
                      borderRadius: '50%',
                      background: c,
                      flexShrink: 0,
                    }}
                  />
                );
              })()}

              {/* Captain label — a small gold pill that sits next to the
                  name so the row reads "★ Pat CAPTAIN" at a glance. The
                  star alone is too subtle; this makes the role explicit
                  without taking up a whole row. */}
              {isCaptain && (
                <span
                  style={{
                    fontSize: 11, fontWeight: 900, letterSpacing: 0.8, textTransform: 'uppercase',
                    color: 'var(--gold)',
                    background: 'rgba(212, 175, 55, 0.12)',
                    border: '1px solid var(--gold)',
                    padding: '2px 6px',
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                >
                  Captain
                </span>
              )}

              {/* HCP slot — ALWAYS rendered with a fixed width so the
                  "H" sits in the same column across every row, whether
                  the player has a handicap, is on a team, or is sitting
                  in Unassigned. Width matches the longest reasonable
                  value ("HCP 54.0"). LEFT-aligned: the "H" pins to the
                  left edge of the slot and the value (0.8, 14.8, 18) is
                  whatever follows, instead of having the whole "HCP X"
                  block right-justify itself and shift the H column-by-
                  column based on value width. */}
              <span
                className="ts-roster-hcp"
                style={{
                  marginLeft: 'auto',
                  width: 68,
                  textAlign: 'left',
                  fontSize: 13, fontWeight: 800, color: 'var(--text-sec)',
                  fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                }}
              >
                {hcp && <>HCP <span style={{ color: 'var(--text)' }}>{hcp}</span></>}
              </span>

              {/* X / placeholder. Unassigned rows still reserve the same
                  width here so the HCP slot ends at the same right
                  offset on every pill — that's what keeps the "H"
                  vertically aligned across the screen. */}
              {showRemove ? (
                <button
                  type="button"
                  onClick={(e) => {
                    // Stop bubble so the parent row tap doesn't fire too.
                    e.stopPropagation();
                    onRemoveFromTeam(p);
                  }}
                  disabled={busy}
                  title="Move to Unassigned"
                  aria-label="Move to Unassigned"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text-mut)',
                    width: 32, height: 32,
                    borderRadius: 8,
                    cursor: busy ? 'wait' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, flexShrink: 0,
                  }}
                >
                  <X width={14} height={14} strokeWidth={2.5} />
                </button>
              ) : (
                <span className="ts-roster-x-placeholder" style={{ width: 32, flexShrink: 0 }} aria-hidden />
              )}
            </div>
          );
        })
      )}
    </div>
    </div>
  );
}
